import { G, Svg } from '@svgdotjs/svg.js';
import {
  ClampJoint,
  CountertopAngleProcessingBase,
  GShapedCountertopAngle,
  GShapedCountertopOutline,
  GShapedCountertopPanel,
  GShapedCountertopSide,
  GShapedCountertopSideData,
  MaterialType,
  PostformingSideEnum,
  gShapedCountertopAngles,
  gShapedCountertopSides,
} from 'cloud-shared-lib';
import _, { isNil } from 'lodash';

import { Rect } from '@svgdotjs/svg.js';
import {
  addTexturePattern,
  createGroupWithElements,
  getPanelMenu,
  getSideSizeEditor,
  maskAnglesProcessing,
  renderAngleSelector,
  renderAnglesEdgeBanding,
  renderAnglesProcessing,
  renderCutouts,
  renderEdgeBanding,
  renderFastenersSets,
  renderHoles,
  renderLines,
  renderLinesJointSelector,
  renderLinesJoints,
  renderPanelSelectors,
  renderPanelsJoints,
  renderPanelsJointsSelectors,
  renderPostforming,
  renderRectSizes,
  renderSideHighlight,
  renderSideSelector,
  renderSlots,
} from '../countertop-render.utils';
import {
  Countertop,
  CountertopLine,
  CountertopLinesJoint,
  CountertopLinesJointTypeEnum,
  CountertopMaterial,
  CountertopTypeEnum,
  GShapedCountertopSubtypeEnum,
  MasonsMitreJointParams,
  RenderOptions,
  SlotAppearanceOverride,
} from '../countertop.interface';
import { CountertopUtils, PanelLengthChangeDirection } from '../countertop.utils';
import { GShapedCountertopLine } from './g-shaped-countertop-line';

export interface GShapedRenderOptions
  extends Omit<RenderOptions, 'edgeBandingColors' | 'disabledSideSelectors' | 'highlightedSides'> {
  edgeBandingColors?: GShapedCountertopSideData<string>;
  highlightedSides?: GShapedCountertopSideData<string>;
  disabledSideSelectors?: GShapedCountertopSide[];
}

/**
 * FullLengthTopWithLeftSide░FullLengthTopWithRightSide\
 * ╔════════════════╗  ╔════════════════╗\
 * ║▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒║  ║▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒║\
 * ║▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒║  ║▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒║\
 * ╠═════╦══════════╝  ╚══════════╦═════╣\
 * ║▒▒▒▒▒║░░░░░░░░░░░  ░░░░░░░░░░░║▒▒▒▒▒║\
 * ║▒▒▒▒▒║░░░░░░░░░░░  ░░░░░░░░░░░║▒▒▒▒▒║\
 * ║▒▒▒▒▒║░░░░░░░░░░░  ░░░░░░░░░░░║▒▒▒▒▒║\
 * ╚═════╝░░░░░░░░░░░  ░░░░░░░░░░░╚═════╝
 *
 * WithFullWidthLeftSide░WithFullWidthRightSide\
 * ╔═════╦══════════╗  ╔══════════╦═════╗\
 * ║▒▒▒▒▒║▒▒▒▒▒▒▒▒▒▒║  ║▒▒▒▒▒▒▒▒▒▒║▒▒▒▒▒║\
 * ║▒▒▒▒▒║▒▒▒▒▒▒▒▒▒▒║  ║▒▒▒▒▒▒▒▒▒▒║▒▒▒▒▒║\
 * ║▒▒▒▒▒╠══════════╝  ╚══════════╣▒▒▒▒▒║\
 * ║▒▒▒▒▒║░░░░░░░░░░░  ░░░░░░░░░░░║▒▒▒▒▒║\
 * ║▒▒▒▒▒║░░░░░░░░░░░  ░░░░░░░░░░░║▒▒▒▒▒║\
 * ║▒▒▒▒▒║░░░░░░░░░░░  ░░░░░░░░░░░║▒▒▒▒▒║\
 * ╚═════╝░░░░░░░░░░░  ░░░░░░░░░░░╚═════╝
 */
export class GShapedCountertop implements Countertop {
  readonly lines: GShapedCountertopLine[] = [];

  id: number;
  subtype: GShapedCountertopSubtypeEnum;

  has3d = false;
  useClampJointForPanels = true;

  linesJoints: GShapedCountertopLinesJoints;
  count = 1;
  outline: GShapedCountertopOutline = new GShapedCountertopOutline();

  readonly type = CountertopTypeEnum.GShaped;

  /**
   * Создает столешницу со всеми линиями и панелями. по умолчанию, ширина линий равна ширине материала.
   * @param length Габаритная Длина столешницы
   * @param width Габаритная ширина столешницы
   * @param material Материал столешницы
   * @param type Тип Г-образной столешницы
   * @param options options
   * @param options.lines
   */
  constructor(
    length: number,
    width: number,
    material: CountertopMaterial,
    type: GShapedCountertopSubtypeEnum,
    options?: Partial<{ lines: GShapedCountertopLine[] }>
  ) {
    this.subtype = type;

    let topLine: GShapedCountertopLine;
    let sideLine: GShapedCountertopLine;

    if (options?.lines) {
      this.lines = options.lines;
      this.sideLine.isVertical = true;
      this.topLine.isVertical = false;
    }

    if (!options?.lines) {
      const maxCountertopPlateMatWidth = 600;
      const linesWidth =
        material.type === MaterialType.Plate ? Math.min(material.width, maxCountertopPlateMatWidth) : material.width;

      switch (type) {
        case GShapedCountertopSubtypeEnum.WithFullWidthLeftSide:
        case GShapedCountertopSubtypeEnum.WithFullWidthRightSide:
          sideLine = new GShapedCountertopLine(width, linesWidth, material, { isVertical: true });
          topLine = new GShapedCountertopLine(length - sideLine.width, linesWidth, material, { isVertical: false });

          break;

        case GShapedCountertopSubtypeEnum.FullLengthTopWithLeftSide:
        case GShapedCountertopSubtypeEnum.FullLengthTopWithRightSide:
          topLine = new GShapedCountertopLine(length, linesWidth, material, { isVertical: false });
          sideLine = new GShapedCountertopLine(width - topLine.width, linesWidth, material, { isVertical: true });

          break;
      }

      switch (type) {
        case GShapedCountertopSubtypeEnum.WithFullWidthLeftSide:
        case GShapedCountertopSubtypeEnum.FullLengthTopWithLeftSide:
          this.lines = [sideLine, topLine];
          break;

        case GShapedCountertopSubtypeEnum.WithFullWidthRightSide:
        case GShapedCountertopSubtypeEnum.FullLengthTopWithRightSide:
          this.lines = [topLine, sideLine];
          break;
      }
    }

    this.linesJoints = new GShapedCountertopLinesJoints(
      () => this.topLine,
      () => this.sideLine,
      this.subtype
    );
  }

  /***********LINES****************/
  get topLine(): GShapedCountertopLine {
    switch (this.subtype) {
      case GShapedCountertopSubtypeEnum.WithFullWidthLeftSide:
      case GShapedCountertopSubtypeEnum.FullLengthTopWithLeftSide:
        return this.lines[1];

      case GShapedCountertopSubtypeEnum.WithFullWidthRightSide:
      case GShapedCountertopSubtypeEnum.FullLengthTopWithRightSide:
        return this.lines[0];
    }
  }
  get sideLine(): GShapedCountertopLine {
    switch (this.subtype) {
      case GShapedCountertopSubtypeEnum.WithFullWidthLeftSide:
      case GShapedCountertopSubtypeEnum.FullLengthTopWithLeftSide:
        return this.lines[0];

      case GShapedCountertopSubtypeEnum.WithFullWidthRightSide:
      case GShapedCountertopSubtypeEnum.FullLengthTopWithRightSide:
        return this.lines[1];
    }
  }
  /***********LINES END****************/

  get material(): CountertopMaterial {
    return this.topLine.material;
  }
  set material(value: CountertopMaterial) {
    this.lines.forEach(l => {
      l.material = value;
    });
  }

  get length(): number {
    if (
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthLeftSide ||
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthRightSide
    ) {
      return this.topLine.length + this.sideLine.width;
    }

    return this.topLine.length;
  }
  set length(value: number) {
    let newTopLineLength = value;

    if (
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthLeftSide ||
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthRightSide
    ) {
      newTopLineLength = Math.max(newTopLineLength - this.sideLine.width, 0);
    } else {
      newTopLineLength = Math.max(newTopLineLength, this.sideLine.width);
    }

    this.topLine.length = newTopLineLength;
  }
  get width(): number {
    if (
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthLeftSide ||
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthRightSide
    ) {
      return this.sideLine.length;
    }

    return this.topLine.width + this.sideLine.length;
  }
  set width(value: number) {
    let newSideLineLength = value;

    if (
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithLeftSide ||
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithRightSide
    ) {
      newSideLineLength = Math.max(newSideLineLength - this.topLine.width, 0);
    } else {
      newSideLineLength = Math.max(newSideLineLength, this.topLine.width);
    }

    this.sideLine.length = newSideLineLength;
  }

  /**************PARAMS*******************/

  setLineWidth(lineId: string, width: number): void {
    if (lineId === this.topLine.uid) this.setTopSideWidth(width);
    if (lineId === this.sideLine.uid) this.setSideLineWidth(width);
  }

  setTopSideWidth(value: number): void {
    const isFullWidthSide =
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthRightSide ||
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthLeftSide;

    CountertopUtils.checkLineWidth(value, this.topLine.material);

    const sideExpandValue = this.topLine.width - value;

    this.topLine.width = value;

    if (!isFullWidthSide) this.sideLine.length += sideExpandValue;
  }

  setSideLineWidth(value: number): void {
    CountertopUtils.checkLineWidth(value, this.sideLine.material);

    this.sideLine.width = value;
  }

  /************PARAMS END****************/

  getAngleNeighbors(angleNumber: GShapedCountertopAngle):
    | {
        leftAngle?: GShapedCountertopAngle;
        rightAngle?: GShapedCountertopAngle;
        leftSide: GShapedCountertopSide;
        rightSide: GShapedCountertopSide;
      }
    | undefined {
    const isLeftSideCountertop =
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithLeftSide ||
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthLeftSide;

    switch (angleNumber) {
      case '1':
        return { leftAngle: '5', rightAngle: '2', leftSide: '6', rightSide: '1' };
      case '2':
        return { leftAngle: '1', rightAngle: '3', leftSide: '1', rightSide: '2' };
      case '3':
        return { leftAngle: '2', rightAngle: isLeftSideCountertop ? undefined : '4', leftSide: '2', rightSide: '3' };
      case '4':
        return {
          leftAngle: isLeftSideCountertop ? undefined : '3',
          rightAngle: isLeftSideCountertop ? '5' : undefined,
          leftSide: isLeftSideCountertop ? '4' : '3',
          rightSide: isLeftSideCountertop ? '5' : '4',
        };
      case '5':
        return { leftAngle: isLeftSideCountertop ? '4' : undefined, rightAngle: '1', leftSide: '5', rightSide: '6' };

      default:
        return;
    }
  }

  getProcessableNeighborAngles(angleNumber: GShapedCountertopAngle): {
    left?: CountertopAngleProcessingBase;
    right?: CountertopAngleProcessingBase;
  } {
    const neighbors = this.getAngleNeighbors(angleNumber);

    return {
      left: this.outline.angleProcessing[neighbors.leftAngle],
      right: this.outline.angleProcessing[neighbors.rightAngle],
    };
  }

  checkSidePostforming(sideN: GShapedCountertopSide): boolean {
    const isLeftSideCountertop =
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithLeftSide ||
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthLeftSide;

    const isBottomPostforming = (m: CountertopMaterial) =>
      m.postforming === PostformingSideEnum.Bottom || m.postforming === PostformingSideEnum.TopAndBottom;

    if (
      (sideN === '1' && this.material.postforming === PostformingSideEnum.TopAndBottom) ||
      (sideN === '4' && isBottomPostforming(this.material))
    ) {
      return true;
    }

    if (isLeftSideCountertop) {
      return (
        (sideN === '6' && this.material.postforming === PostformingSideEnum.TopAndBottom) ||
        (sideN === '3' && isBottomPostforming(this.material))
      );
    }

    if (!isLeftSideCountertop) {
      return (
        (sideN === '2' && this.material.postforming === PostformingSideEnum.TopAndBottom) ||
        (sideN === '5' && isBottomPostforming(this.material))
      );
    }

    return false;
  }

  setPanelLength(value: number, lineUid: string, panelUid: string, direction: PanelLengthChangeDirection): void {
    const line: GShapedCountertopLine = this.lines.find(l => l.uid === lineUid);

    if (!line) {
      throw new Error(`Линия не найдена`);
    }

    const panel: GShapedCountertopPanel = line.panels.find(p => p.uid === panelUid);

    if (!panel) {
      throw new Error(`Панель не найдена`);
    }

    CountertopUtils.setPanelLength(value, panel, line, direction);
  }

  deletePanel(lineUid: string, panelUid: string): number {
    const line: GShapedCountertopLine = this.lines.find(l => l.uid === lineUid);

    if (!line) {
      throw new Error(`Линия не найдена`);
    }

    const panel: GShapedCountertopPanel = line.panels.find(p => p.uid === panelUid);

    if (!panel) {
      throw new Error(`Панель не найдена`);
    }

    return CountertopUtils.removePanel(this, line, line.panels, panel);
  }

  canSetWidth(width: number): boolean {
    return this.topLine.width <= width;
  }

  canSetLength(length: number): boolean {
    return this.sideLine.width <= length;
  }

  async render(
    draw: Svg,
    scaled,
    onPanelSizeChange: (
      countertop: GShapedCountertop,
      line: GShapedCountertopLine,
      panel: GShapedCountertopPanel,
      side: 'height' | 'width',
      value: number,
      error?: string
    ) => void,
    onPanelsCountChange: (
      countertop: GShapedCountertop,
      line: GShapedCountertopLine,
      panel: GShapedCountertopPanel,
      mode: 'add' | 'delete'
    ) => void,
    onAngleSelectorClick: (angleNumber: number, angleProcessing: CountertopAngleProcessingBase) => void,
    onSideSelectorClick: (sideNumber: number) => void,
    onLinesJointSelectorClick: (joint: GShapedCountertopLinesJoint) => void,
    onClampJointClick?: (joint: ClampJoint) => void,
    options?: GShapedRenderOptions
  ): Promise<G[]> {
    const lines = renderLines(this.lines);

    const lineGroups = this.linesPositioning(lines);

    const panelsJoints = this.renderPanelsJoints();

    const postforming = this.renderPostforming();

    const anglesProcessing = maskAnglesProcessing(
      createGroupWithElements(this.renderAnglesProcessing()),
      lineGroups,
      draw
    );

    const linesJoint = this.renderLinesJoints();

    const cutouts = await renderCutouts(this.outline.cutouts, this.length, this.width);

    const fastenerSets = renderFastenersSets(this.outline.fastenersSets, this.length, this.width);
    const holes = createGroupWithElements(renderHoles(this.outline.customHoles, this.length, this.width));

    const slots = this.renderSlots(options?.slotColors);

    const edgeBanding = this.renderEdgeBanding(options?.edgeBandingColors);
    const anglesEdgeBanding = this.renderAnglesEdgeBanding();

    const highlightedSides = _.isNil(options?.highlightedSides)
      ? []
      : this.renderHighlightedSides(options.highlightedSides);

    const panelSelectors = options?.panelSelectors
      ? this.renderPanelSelectors(draw, scaled, onPanelSizeChange, onPanelsCountChange, {
          panelSplit: options?.panelSplit,
          panelRemoving: options?.panelRemoving,
        })
      : [];

    if (options.panelSizes) {
      const line = this.lines.find(l => l.panels.some(p => p.uid === options.panelSizes));
      const panel = line.panels.find(p => p.uid === options.panelSizes);

      const lineGroup = panelSelectors.find(lg => lg.attr().lineId === line.uid);
      if (line) {
        this.renderPanelSizes(
          draw,
          line.uid,
          lineGroup.findOne(`[panelId="${panel.uid}"]`) as Rect,
          scaled,
          onPanelSizeChange
        ).addTo(lineGroup);
      }
    }

    const sideSelectors = options?.sideSelectors
      ? this.renderSideSelectors(onSideSelectorClick, options.disabledSideSelectors)
      : [];
    const linesJointSelectors = options?.linesJointSelectors
      ? this.renderLinesJointSelectors(onLinesJointSelectorClick, onClampJointClick)
      : [];
    const angleSelectors = options?.angleSelectors ? this.renderAngleSelectors(onAngleSelectorClick) : [];

    const panelsJointsSelectors = options?.panelsJointsSelector
      ? this.renderPanelsJointsSelectors(onClampJointClick)
      : [];

    const renderGroups = [
      ...lineGroups,
      ...panelsJoints,
      ...linesJoint,
      ...slots,
      ...postforming,
      ...edgeBanding,
      ...highlightedSides,
      anglesProcessing,
      ...anglesEdgeBanding,
      ...cutouts,
      ...fastenerSets,
      holes,
      ...panelSelectors,
      ...sideSelectors,
      ...linesJointSelectors,
      ...angleSelectors,
      ...panelsJointsSelectors,
    ];
    renderGroups.forEach(l => draw.add(l));

    return renderGroups;
  }

  async renderTextureSchema(draw: Svg): Promise<G[]> {
    const lines = renderLines(this.lines);

    const topFaceBanding = _.maxBy(this.outline.faceBanding, b => b.level);
    const topMaterial = !isNil(topFaceBanding) && topFaceBanding.level > 0 ? topFaceBanding.material : this.material;

    addTexturePattern(draw, topMaterial.color ? `#${topMaterial.color}` : null, {
      ...topMaterial.textureSettings,
      texturePath: topMaterial.textureLink,
    }).then(p => {
      lineGroups.forEach(g => {
        g.children().each(e => {
          if (e.attr().type === 'line') {
            e.fill(p);
          }
        });
      });
    });

    const lineGroups = this.linesPositioning(lines);

    const anglesProcessing = maskAnglesProcessing(
      createGroupWithElements(this.renderAnglesProcessing()),
      lineGroups,
      draw
    );

    const cutouts = await renderCutouts(this.outline.cutouts, this.length, this.width);

    const renderGroups = [...lineGroups, anglesProcessing, ...cutouts];
    renderGroups.forEach(l => draw.add(l));

    return renderGroups;
  }

  private renderSlots(appearanceOverride?: SlotAppearanceOverride[]): G[] {
    const isRightSide =
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithRightSide ||
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthRightSide;

    const slotGroups = renderSlots(this.outline.slots, this.getSideLengthByNumber.bind(this), appearanceOverride);

    slotGroups.forEach(g => {
      const sideN = g.attr().slotSide.toString();

      if (sideN === '2') {
        g.children().forEach(s =>
          s
            .rotate(-90, 0, 0)
            .x(isRightSide ? -this.width : -this.topLine.width)
            .flip('x', this.length / 2)
        );
      }

      if (sideN === '3') {
        g.children().forEach(s =>
          s.flip('y', isRightSide ? this.width / 2 : this.topLine.width / 2).flip('x', this.length / 2)
        );
      }

      if (sideN === '4') {
        g.children().forEach(s =>
          s
            .rotate(-90, 0, 0)
            .x(-this.width)
            .dy(isRightSide ? this.length - this.sideLine.width : 0)
            .flip('x', isRightSide ? undefined : this.sideLine.width / 2)
        );
      }

      if (sideN === '5') {
        g.children().forEach(s => s.flip('y', isRightSide ? this.topLine.width / 2 : this.width / 2));
      }

      if (sideN === '6') {
        g.children().forEach(s => s.rotate(-90, 0, 0).x(isRightSide ? -this.topLine.width : -this.width));
      }
    });

    return slotGroups;
  }

  getLineByAngleNumber(angleNumber: number): GShapedCountertopLine {
    const isRightSide =
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithRightSide ||
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthRightSide;

    switch (angleNumber) {
      case 1:
        return this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthLeftSide ? this.sideLine : this.topLine;
      case 2:
        return this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthRightSide ? this.sideLine : this.topLine;
      case 3:
        return isRightSide ? this.sideLine : this.topLine;
      case 4:
        return this.sideLine;
      case 5:
        return isRightSide ? this.topLine : this.sideLine;
    }
  }

  getSideLengthByNumber(n: string): number {
    const isRightSide =
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithRightSide ||
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthRightSide;

    const isFullLengthTop =
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithLeftSide ||
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithRightSide;

    switch (n) {
      case '1':
        return this.length;
      case '2':
        return isRightSide ? this.width : this.topLine.width;
      case '3':
        return isRightSide
          ? this.sideLine.width
          : this.topLine.length -
              (this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithLeftSide ? this.sideLine.width : 0);
      case '4':
        return this.sideLine.length - (isFullLengthTop ? 0 : this.topLine.width);
      case '5':
        return isRightSide
          ? this.topLine.length -
              (this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithRightSide ? this.sideLine.width : 0)
          : this.sideLine.width;
      case '6':
        return isRightSide ? this.topLine.width : this.width;
    }
  }

  getNeighborsByAxis(angleNumber: GShapedCountertopAngle, axis: 'x' | 'y'): { angle: string; side: string } {
    const angleNeighbors = this.getAngleNeighbors(angleNumber);

    const isRightSide =
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithRightSide ||
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthRightSide;

    switch (+angleNumber) {
      case 1:
      case 3:
        return axis === 'x'
          ? { angle: angleNeighbors.rightAngle, side: angleNeighbors.rightSide }
          : { angle: angleNeighbors.leftAngle, side: angleNeighbors.leftSide };

      case 5:
      case 2:
        return axis === 'x'
          ? { angle: angleNeighbors.leftAngle, side: angleNeighbors.leftSide }
          : { angle: angleNeighbors.rightAngle, side: angleNeighbors.rightSide };

      case 4:
        if (axis === 'x') {
          return isRightSide
            ? { angle: angleNeighbors.leftAngle, side: angleNeighbors.leftSide }
            : { angle: angleNeighbors.rightAngle, side: angleNeighbors.rightSide };
        } else {
          return isRightSide
            ? { angle: angleNeighbors.rightAngle, side: angleNeighbors.rightSide }
            : { angle: angleNeighbors.leftAngle, side: angleNeighbors.leftSide };
        }
    }
  }

  private renderPostforming(): G[] {
    const isRightSide =
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithRightSide ||
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthRightSide;

    if (!this.material.postforming) {
      return [new G()];
    }

    const postforming = this.material.postforming;
    const isBottomPostforming = postforming === PostformingSideEnum.Bottom;

    const topSideN = isBottomPostforming ? (isRightSide ? '5' : '3') : '1';
    const sideN = isBottomPostforming ? '4' : isRightSide ? '2' : '6';

    const topSideLength = this.getSideLengthByNumber(topSideN);
    const sideLength = this.getSideLengthByNumber(sideN);

    const top = createGroupWithElements([renderPostforming(topSideLength).attr({ postforming: 'top' })]).attr({
      type: 'postforming',
    });
    const side = createGroupWithElements([renderPostforming(sideLength).attr({ postforming: 'side' })]).attr({
      type: 'postforming',
    });

    this.sidePositioning(topSideN, top);
    this.sidePositioning(sideN, side);

    return [top, side];
  }

  private linesPositioning(lines: G[]) {
    return lines.map(l => {
      if (l.attr().lineId == this.topLine.uid) {
        this.topLinePositioning(l);
      }

      if (l.attr().lineId == this.sideLine.uid) {
        this.sideLinePositioning(l);
      }

      return l;
    });
  }

  private renderAnglesProcessing(): G[] {
    const anglesProcessing = renderAnglesProcessing(this.outline.angleProcessing);
    anglesProcessing.forEach(p => {
      this.angleProcessingPositioning(p.attr().angleName.toString(), p);
    });

    return anglesProcessing;
  }

  private angleProcessingPositioning(angleName: string, group: G): G {
    const isRightSide =
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithRightSide ||
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthRightSide;

    switch (angleName) {
      case '1': {
        return group.center(0, 0);
      }

      case '2': {
        return group.center(this.length, 0).flip('x');
      }

      case '3': {
        return group.center(this.length, isRightSide ? this.width : this.topLine.width).flip('both');
      }

      case '4': {
        return group
          .center(isRightSide ? this.length - this.sideLine.width : this.sideLine.width, this.width)
          .flip(isRightSide ? 'y' : 'both');
      }

      case '5': {
        return group.center(0, isRightSide ? this.topLine.width : this.width).flip('y');
      }

      default:
        return group;
    }
  }

  private topLinePositioning(rect: G): void {
    if (this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthLeftSide) {
      rect.move(this.sideLine.width, 0);
    } else {
      rect.move(0, 0);
    }
  }

  private sideLinePositioning(rect: G): void {
    let x = 0;
    let y = 0;

    if (
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithLeftSide ||
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithRightSide
    ) {
      y = this.topLine.width;
    }

    if (
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthRightSide ||
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithRightSide
    ) {
      x = this.length - this.sideLine.width;
    }

    rect.move(x, y);

    this.rotateSideLine(rect);
  }

  private rotateSideLine(rect: G): void {
    const rotateCenterX = +rect.x().valueOf() + +rect.width().valueOf() / 2;
    const rotateCenterY = +rect.y().valueOf() + +rect.width().valueOf() / 2;

    rect.rotate(-90, rotateCenterX, rotateCenterY);
  }

  private sidePositioning(sideN: GShapedCountertopSide, group: G): void {
    const isLeftSideCountertop =
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithLeftSide ||
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthLeftSide;

    const isFullWidthSide =
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthRightSide ||
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthLeftSide;

    switch (sideN) {
      case '2':
        group.rotate(90, 0, 0).dy(-this.length);
        break;

      case '3':
        if (isLeftSideCountertop) {
          group.dy(this.topLine.width).dx(this.sideLine.width);
        } else {
          group.flip('y', this.width / 2).flip('x', this.length / 2);
        }
        break;

      case '4':
        if (isLeftSideCountertop) {
          group.rotate(90, 0, 0).dmove(this.topLine.width, -this.sideLine.width);
        } else {
          group.rotate(-90, 0, 0).dmove(-this.width, this.length - this.sideLine.width);
        }
        break;

      case '5':
        if (isLeftSideCountertop) {
          group.flip('y', this.width / 2).flip('x');
        } else {
          group.dy(this.topLine.width);
        }
        break;

      case '6':
        group.rotate(-90, 0, 0).dx(isLeftSideCountertop ? -this.width : -this.topLine.width);
        break;
    }
  }

  private renderEdgeBanding(colors?: GShapedCountertopSideData<string>): G[] {
    return _(this.outline.edgeBanding)
      .map((v, k: GShapedCountertopSide) => (v ? { n: k, value: v } : null))
      .compact()
      .map(({ n, value }) => {
        const group = renderEdgeBanding(n, this.getSideLengthByNumber(n), value, colors?.[n]);
        this.sidePositioning(n, group);

        return group;
      })
      .value();
  }

  private renderAnglesEdgeBanding(): G[] {
    return _(this.outline.angleProcessing)
      .map((v, k: GShapedCountertopSide) => (v ? { n: k, value: v } : null))
      .compact()
      .filter(v => !!v.value?.parameters?.edgeBanding)
      .map(({ n, value }) => {
        const group = renderAnglesEdgeBanding(value);

        this.angleProcessingPositioning(n, group);

        return group;
      })
      .value();
  }

  private renderHighlightedSides(colors?: GShapedCountertopSideData<string>): G[] {
    return _(colors)
      .map((v, k: GShapedCountertopSide) => (v ? { n: k, value: v } : null))
      .compact()
      .map(({ n, value }) => {
        const group = createGroupWithElements([renderSideHighlight(this.getSideLengthByNumber(n), value)]);
        this.sidePositioning(n, group);

        return group;
      })
      .value();
  }

  private renderLinesJointSelectors(
    onClick: (joint: GShapedCountertopLinesJoint) => void,
    onConnectorClick?: (connector: ClampJoint) => void
  ) {
    const joints = this.linesJoints.map(j =>
      renderLinesJointSelector(
        j,
        () => {
          onClick(j);
        },
        () => {
          onConnectorClick?.(j.connector);
        }
      )
    );

    joints.forEach(j => this.linesJointPositioning(j));

    return joints;
  }

  private renderAngleSelectors(
    onClick: (angleNumber: number, angleProcessing: CountertopAngleProcessingBase) => void
  ): G[] {
    const angleSelectors = gShapedCountertopAngles.map(n =>
      renderAngleSelector(
        +n,
        this.outline.angleProcessing[n],
        Math.min(this.topLine.width, this.topLine.length) / 3,
        onClick
      )
    );

    angleSelectors.forEach(p => {
      this.angleProcessingPositioning(p.attr().angleName.toString(), p);
    });

    return angleSelectors;
  }

  private linesJointPositioning(joint: G): void {
    // const { male, female } = joint.attr() as Record<string, string>;

    const orientation =
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithLeftSide ||
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithRightSide
        ? 'horizontal'
        : 'vertical';

    const horizontalLeft =
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithLeftSide && orientation === 'horizontal';
    const horizontalRight =
      this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithRightSide && orientation === 'horizontal';
    const verticalLeft =
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthLeftSide && orientation === 'vertical';
    const verticalRight =
      this.subtype === GShapedCountertopSubtypeEnum.WithFullWidthRightSide && orientation === 'vertical';

    if (horizontalLeft) {
      joint.flip('x').cy(this.topLine.width);
    }

    if (horizontalRight) {
      joint.cy(this.topLine.width).x(this.length - this.sideLine.width);
    }

    if (verticalLeft) {
      joint.rotate(-90, 0, 0).dx(-joint.bbox().width).cy(this.sideLine.width);
    }

    if (verticalRight) {
      joint
        .rotate(-90, 0, 0)
        .dx(-joint.bbox().width)
        .cy(this.length - this.sideLine.width)
        .flip('x', joint.cy());
    }
  }

  private renderSideSelectors(onClick: (sideN: number) => void, disabledSides: GShapedCountertopSide[] = []): G[] {
    const groups = _.difference(gShapedCountertopSides, disabledSides).map(n => {
      const selector = renderSideSelector(+n, this.getSideLengthByNumber(n), onClick);

      this.sidePositioning(n, selector);

      return selector;
    });

    return groups;
  }

  private renderLinesJoints(): G[] {
    const joints = renderLinesJoints(this.linesJoints);

    joints.forEach(j => this.linesJointPositioning(j));

    return joints;
  }

  private renderPanelsJoints(): G[] {
    const hasJoint = this.lines.some(l => l.panelsJoints.every(c => !isNil(c.connector)));

    if (!hasJoint || !this.useClampJointForPanels) return [];

    return this.linesPositioning(this.lines.map(l => renderPanelsJoints(l)));
  }

  private renderPanelsJointsSelectors(onClick?: (connector: ClampJoint) => void): G[] {
    const hasJoint = this.lines.some(l => l.panelsJoints.every(c => !isNil(c.connector)));

    if (!hasJoint || !this.useClampJointForPanels) return [];

    const joints = this.lines.map(l => renderPanelsJointsSelectors(l, onClick));

    return this.linesPositioning(joints);
  }

  private renderPanelSelectors(
    draw: Svg,
    scaled: number,
    onPanelSizeChange: (
      countertop: GShapedCountertop,
      line: GShapedCountertopLine,
      panel: GShapedCountertopPanel,
      side: 'height' | 'width',
      value: number,
      error?: string
    ) => void,
    onPanelsCountChange: (
      countertop: GShapedCountertop,
      line: GShapedCountertopLine,
      panel: GShapedCountertopPanel,
      mode: 'add' | 'delete'
    ) => void,
    options?: { panelSplit?: boolean; panelRemoving?: boolean }
  ): G[] {
    const lineGroups = renderPanelSelectors(this.lines, (rect, lineGroup) => {
      const elementsToDelete = draw.find('[type="panelSizes"],[type="actions"]');
      _.invokeMap(elementsToDelete, 'remove');

      const gg = lineGroup.attr();

      const lineId = lineGroup.attr().lineId;
      const isVerticalLine = lineId !== this.topLine.uid;

      {
        const line = this.lines.find(l => l.uid === lineId);
        const panel = line?.panels.find(p => p.uid === rect.attr().panelId);
        const { panelSplit, panelRemoving } = options ?? {};

        getPanelMenu(this, line, panel, scaled, onPanelsCountChange, { panelSplit, panelRemoving })
          .center(rect.cx(), rect.cy())
          .rotate(isVerticalLine ? 90 : 0)
          .addTo(lineGroup);
      }

      this.renderPanelSizes(draw, lineId, rect, scaled, onPanelSizeChange).addTo(lineGroup);
    });

    this.linesPositioning(lineGroups);

    return lineGroups;
  }

  renderPanelSizes(
    draw: Svg,
    lineId: string,
    rect: Rect,
    scaled: number,
    onPanelSizeChange: (
      countertop: GShapedCountertop,
      line: GShapedCountertopLine,
      panel: GShapedCountertopPanel,
      side: 'height' | 'width',
      value: number,
      error?: string
    ) => void
  ): G {
    const isVerticalLine = lineId !== this.topLine.uid;

    return createGroupWithElements(
      renderRectSizes(
        rect,
        scaled,
        this.width,
        this.length,
        lineId === this.topLine.uid ? 'ltr' : 'rtl',
        [
          lineId === this.sideLine.uid && this.subtype === GShapedCountertopSubtypeEnum.FullLengthTopWithLeftSide
            ? 'bottom'
            : 'top',
          'left',
        ],
        (t, panelGroup) => {
          const elementsToDelete = draw.find('foreignObject[type="sizeSettings"]');
          _.invokeMap(elementsToDelete, 'remove');

          {
            const sizeSide: 'width' | 'height' = panelGroup.attr().side;

            const line = this.lines.find(l => l.uid === lineId);
            const panel = line?.panels.find(p => p.uid === rect.attr().panelId);

            const editor = getSideSizeEditor(
              this,
              line,
              panel,
              sizeSide,
              `${t.bbox().height}px`,
              t.bbox().width * 3,
              t.bbox().height * 1.5,
              +t.text(),
              onPanelSizeChange
            )
              .center(t.cx(), t.cy())
              .rotate((isVerticalLine && sizeSide === 'height') || (!isVerticalLine && sizeSide === 'width') ? 90 : 0)
              .addTo(panelGroup);

            editor.node.querySelector('input')?.select();
          }
        }
      )
    ).attr({ type: 'panelSizes' });
  }
}

export class GShapedCountertopLinesJoints extends Array<GShapedCountertopLinesJoint> {
  constructor(
    topLine: () => GShapedCountertopLine,
    sideLine: () => GShapedCountertopLine,
    countertopSubtype: GShapedCountertopSubtypeEnum
  ) {
    super();

    const isFullWidthSide =
      countertopSubtype === GShapedCountertopSubtypeEnum.WithFullWidthRightSide ||
      countertopSubtype === GShapedCountertopSubtypeEnum.WithFullWidthLeftSide;

    const leftJointMF: [() => GShapedCountertopLine, () => GShapedCountertopLine] = isFullWidthSide
      ? [topLine, sideLine]
      : [sideLine, topLine];

    this.push(new GShapedCountertopLinesJoint(...leftJointMF, CountertopLinesJointTypeEnum.None, null, null));
  }
}

export class GShapedCountertopLinesJoint implements CountertopLinesJoint {
  constructor(
    private getMaleLine: () => GShapedCountertopLine,
    private getFemaleLine: () => GShapedCountertopLine,
    public type: CountertopLinesJointTypeEnum,
    public params: MasonsMitreJointParams | null,
    public connector: ClampJoint | null
  ) {}

  get male(): CountertopLine {
    return this.getMaleLine();
  }
  get female(): CountertopLine {
    return this.getFemaleLine();
  }
}
